import { useState } from "react";
import { NOTE_CLASS } from "../../lib/ui.js";
import {
    ESCALATABLE_ROLE_LABEL,
    ESCALATION_STATES,
    WINDOW_PRESETS,
} from "../../lib/escalationClient.js";
import { displayState, formatAgo, formatWindow, shortId } from "../../lib/escalationLabels.js";
import { Action, Countdown, StateChip, TargetChip } from "./EscalationParts.jsx";

const INPUT_CLASS = "min-h-[30px] w-full border border-[color:var(--line)] bg-transparent px-[8px] text-[12px] leading-[1.18] text-[color:var(--ink)] outline-none focus:border-[color:var(--line-strong)]";

function ApprovePanel({ request, busy, onConfirm, onCancel }) {
    const [seconds, setSeconds] = useState(request.requestedSeconds);
    const [note, setNote] = useState("");
    const options = WINDOW_PRESETS.filter((preset) => preset.seconds < request.requestedSeconds);
    return (
        <div className="grid gap-[9px] border-t border-[color:var(--line-soft)] pt-[10px]">
            <div className="grid gap-[5px]">
                <span className="text-[10px] uppercase tracking-[0.12em] text-[color:var(--muted-soft)]">
                    Access duration
                </span>
                <div className="flex flex-wrap gap-[6px]">
                    {[{ seconds: request.requestedSeconds, label: `${formatWindow(request.requestedSeconds)} (requested)` }, ...options].map((option) => (
                        <button
                            key={option.seconds}
                            type="button"
                            aria-pressed={seconds === option.seconds}
                            onClick={() => setSeconds(option.seconds)}
                            className={[
                                "border px-[9px] py-[4px] text-[11px] leading-[1.1] transition-colors cursor-pointer",
                                seconds === option.seconds
                                    ? "border-[color:var(--green)] bg-[color:var(--green-soft)] text-[color:var(--ink)]"
                                    : "border-[color:var(--line)] text-[color:var(--muted)] hover:border-[color:var(--line-strong)] hover:text-[color:var(--ink)]",
                            ].join(" ")}
                        >
                            {option.label}
                        </button>
                    ))}
                </div>
                <p className={`${NOTE_CLASS} m-0 text-[11px]`}>
                    Approve the requested duration or choose a shorter one.
                </p>
            </div>
            <input
                type="text"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="Decision note (optional)"
                aria-label="Decision note"
                className={INPUT_CLASS}
            />
            <div className="flex items-center justify-end gap-[8px]">
                <Action onClick={onCancel}>Back</Action>
                <Action
                    tone="primary"
                    disabled={busy}
                    onClick={() => onConfirm({ approve: true, grantedSeconds: seconds, note })}
                >
                    {busy ? "Approving..." : `Approve ${formatWindow(seconds)}`}
                </Action>
            </div>
        </div>
    );
}

function DenyPanel({ busy, onConfirm, onCancel }) {
    const [note, setNote] = useState("");
    return (
        <div className="grid gap-[9px] border-t border-[color:var(--line-soft)] pt-[10px]">
            <input
                type="text"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="Reason for denial (visible to requester)"
                aria-label="Denial note"
                className={INPUT_CLASS}
            />
            <div className="flex items-center justify-end gap-[8px]">
                <Action onClick={onCancel}>Back</Action>
                <Action tone="danger" disabled={busy} onClick={() => onConfirm({ approve: false, note })}>
                    {busy ? "Denying..." : "Deny request"}
                </Action>
            </div>
        </div>
    );
}

export default function EscalationCard({
    request,
    endpointNames,
    subjectNames,
    isOwn,
    canDecide,
    busy = false,
    error = null,
    onDecide,
    onCancel,
    onRevoke,
}) {
    const [panel, setPanel] = useState(null);
    const state = displayState(request);
    const live = state === ESCALATION_STATES.APPROVED;
    const pending = state === ESCALATION_STATES.PENDING;
    const approvable = pending && Boolean(request.requestedRoleId);

    return (
        <article className="grid min-w-0 gap-[8px] border-b border-[color:var(--line-soft)] p-[12px] last:border-b-0">
            <header className="flex flex-wrap items-baseline justify-between gap-x-[12px] gap-y-[4px]">
                <div className="flex min-w-0 flex-wrap items-baseline gap-x-[10px] gap-y-[3px]">
                    <span className="break-all text-[12px] font-medium text-[color:var(--ink)]" title={request.requesterUserId}>
                        {isOwn ? "You" : request.requesterDisplay || shortId(request.requesterUserId)}
                    </span>
                    <StateChip state={state} />
                    {live && request.expiresAt ? <Countdown expiresAt={request.expiresAt} /> : null}
                </div>
                <span className={`${NOTE_CLASS} text-[10px]`}>
                    {formatAgo(request.createdAt)}
                </span>
            </header>

            <TargetChip
                target={request.target}
                endpointNames={endpointNames}
                subjectNames={subjectNames}
            />

            <p className="m-0 break-words text-[12px] leading-[1.5] text-[color:var(--ink)] whitespace-pre-wrap">
                {request.justification}
            </p>

            <div className="flex flex-wrap items-center justify-between gap-x-[16px] gap-y-[8px]">
                <div className={`${NOTE_CLASS} flex flex-wrap gap-x-[16px] gap-y-[2px] text-[11px]`}>
                    <span>
                        {request.requestedRoleId ? ESCALATABLE_ROLE_LABEL : "Missing role: cannot approve"}
                    </span>
                    <span>{formatWindow(request.requestedSeconds)} requested</span>
                    {request.expiresAt && !live ? (
                        <span>Expiry: {request.expiresAt.toLocaleString()}</span>
                    ) : null}
                </div>
                {!panel && ((pending && (isOwn || canDecide)) || (live && canDecide)) ? (
                    <div className="ml-auto flex flex-wrap items-center justify-end gap-[8px]">
                        {pending && isOwn ? (
                            <Action disabled={busy} onClick={onCancel}>Withdraw</Action>
                        ) : null}
                        {pending && canDecide ? (
                            <>
                                <Action disabled={busy} onClick={() => setPanel("deny")}>Deny</Action>
                                <Action
                                    tone="primary"
                                    disabled={busy || !approvable}
                                    onClick={() => setPanel("approve")}
                                >
                                    Approve
                                </Action>
                            </>
                        ) : null}
                        {live && canDecide ? (
                            <Action tone="danger" disabled={busy} onClick={onRevoke}>Revoke access</Action>
                        ) : null}
                    </div>
                ) : null}
            </div>

            {request.decisionNote ? (
                <p className={`${NOTE_CLASS} m-0 text-[11px] leading-[1.45]`}>
                    Decision note: <span className="text-[color:var(--ink)]">{request.decisionNote}</span>
                </p>
            ) : null}

            {error ? (
                <p className="m-0 text-[11px] leading-[1.4] text-red-400">{error}</p>
            ) : null}

            {panel === "approve" ? (
                <ApprovePanel
                    request={request}
                    busy={busy}
                    onConfirm={(decision) => { setPanel(null); onDecide?.(decision); }}
                    onCancel={() => setPanel(null)}
                />
            ) : panel === "deny" ? (
                <DenyPanel
                    busy={busy}
                    onConfirm={(decision) => { setPanel(null); onDecide?.(decision); }}
                    onCancel={() => setPanel(null)}
                />
            ) : null}
        </article>
    );
}
